import React, { useEffect, useRef, useState } from "react";
import {
    Toast,
    RadioButton,
    BrowseButton,
    StandardModal,
    StandardModalActionButton,
    StandardModalActionGroup
} from "src/components";
import {
    createPlaylist,
    deletePlaylist,
    updatePlaylist,
    uploadPlaylistImage
} from "../api";
import {
    ModalContent,
    HelperText,
    FormGrid,
    Field,
    Label,
    TextInput,
    TextArea,
    VisibleToRow,
    UploadArea,
    UploadCircle,
    ErrorText,
    createInitialForm,
    getErrorMessage
} from "./shared";
import GalleryPlaceholder from "src/img/gallery-placeholder.svg";

export default function PlaylistFormModal({
    playlist,
    onClose,
    onSaved,
    onDeleted
}) {
    const isEditMode = Boolean(playlist);
    const fileInputRef = useRef(null);
    const [form, setForm] = useState(createInitialForm(playlist));
    const [imageFile, setImageFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(playlist?.imageUrl || "");
    const [errors, setErrors] = useState({});
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        return () => {
            if (previewUrl && previewUrl.startsWith("blob:")) {
                URL.revokeObjectURL(previewUrl);
            }
        };
    }, [previewUrl]);

    const setAudience = (type) => {
        if (type === "employees") {
            setForm(prev => ({ ...prev, visibleToEmployees: true, visibleToClients: false }));
        }
        if (type === "clients") {
            setForm(prev => ({ ...prev, visibleToEmployees: false, visibleToClients: true }));
        }
        if (type === "both") {
            setForm(prev => ({ ...prev, visibleToEmployees: true, visibleToClients: true }));
        }
    };

    const validate = () => {
        const nextErrors = {};

        if (!form.name.trim()) nextErrors.name = "Title is required.";
        if (!form.description.trim()) nextErrors.description = "Description is required.";
        if (!form.spotifyUri.trim() && !form.appleMusicUrl.trim()) {
            nextErrors.links = "Add at least one Spotify URI or Apple Music URL.";
        }
        if (!form.visibleToEmployees && !form.visibleToClients) {
            nextErrors.visibleTo = "Select who can see this playlist.";
        }
        if (!imageFile && !form.imageUrl) {
            nextErrors.image = "An image is required.";
        }

        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    const handleFileChange = (event) => {
        const file = event.target.files && event.target.files[0];
        if (!file) return;

        if (previewUrl && previewUrl.startsWith("blob:")) {
            URL.revokeObjectURL(previewUrl);
        }

        setImageFile(file);
        setPreviewUrl(URL.createObjectURL(file));
        setErrors(prev => ({ ...prev, image: null }));
    };

    const handleSave = async () => {
        if (!validate()) return;

        setIsSaving(true);
        try {
            let imageUrl = form.imageUrl;
            if (imageFile) {
                imageUrl = await uploadPlaylistImage(imageFile);
            }

            const payload = {
                name: form.name.trim(),
                description: form.description.trim(),
                spotifyUri: form.spotifyUri.trim(),
                appleMusicUrl: form.appleMusicUrl.trim(),
                imageUrl,
                visibleToEmployees: form.visibleToEmployees,
                visibleToClients: form.visibleToClients
            };

            if (isEditMode) {
                await updatePlaylist(playlist.uuid, payload);
                Toast.success("Playlist updated.");
            } else {
                await createPlaylist(payload);
                Toast.success("Playlist created.");
            }

            onSaved();
        } catch (error) {
            Toast.error(getErrorMessage(error, "Unable to save playlist."));
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!playlist?.isCompanyList) return;
        setIsSaving(true);
        try {
            await deletePlaylist(playlist.uuid);
            Toast.success("Playlist deleted.");
            onDeleted();
        } catch (error) {
            Toast.error(getErrorMessage(error, "Unable to delete playlist."));
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <StandardModal
            open={true}
            onClose={onClose}
            title={isEditMode ? "Edit Playlist" : "Create Playlist"}
            description={isEditMode
                ? "Edit your custom playlist for your Me Business app. Change a title, description and image."
                : "Create a custom playlist for your Me Business app. Add a title, description and image, then link a Spotify or Apple Music playlist and choose who can see it."}
            footer={(
                <div style={{ display: "flex", justifyContent: "space-between", width: "100%", gap: "0.75rem" }}>
                    <div>
                        {isEditMode && playlist.isCompanyList && <StandardModalActionButton type="button" $variant="danger" onClick={handleDelete} disabled={isSaving}>Delete Playlist</StandardModalActionButton>}
                    </div>
                    <StandardModalActionGroup $pushRight>
                        <StandardModalActionButton type="button" onClick={onClose} disabled={isSaving}>Cancel</StandardModalActionButton>
                        <StandardModalActionButton type="button" $variant="primary" onClick={handleSave} disabled={isSaving}>{isSaving ? "Saving..." : "Save"}</StandardModalActionButton>
                    </StandardModalActionGroup>
                </div>
            )}
        >
            <ModalContent>
                <FormGrid>
                    <div>
                        <Field>
                            <Label>Title</Label>
                            <TextInput
                                value={form.name}
                                placeholder="Enter the title of this playlist"
                                onChange={({ target: { value } }) => setForm(prev => ({ ...prev, name: value }))}
                            />
                            {errors.name && <ErrorText>{errors.name}</ErrorText>}
                        </Field>
                        <Field>
                            <Label>Description</Label>
                            <TextArea
                                value={form.description}
                                placeholder="Enter a description for this playlist"
                                onChange={({ target: { value } }) => setForm(prev => ({ ...prev, description: value }))}
                            />
                            {errors.description && <ErrorText>{errors.description}</ErrorText>}
                        </Field>
                    </div>
                    <UploadArea>
                        <Label>Upload Image</Label>
                        <UploadCircle>
                            { previewUrl ?
                                <img
                                    src={previewUrl}
                                    alt="Playlist preview"
                                />
                                :
                                <div style={{ display: "block" }}>
                                    <img
                                        src={GalleryPlaceholder}
                                        alt="Playlist preview"
                                        style={{ width: "106px", height: "83px", margin: "auto" }}
                                    />
                                </div>
                            }
                        </UploadCircle>
                        <BrowseButton type="button" style={{ marginTop: "0.5rem" }} onClick={() => fileInputRef.current && fileInputRef.current.click()}>
                            Browse File
                        </BrowseButton>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            style={{ display: "none" }}
                            onChange={handleFileChange}
                        />
                        {errors.image && <ErrorText>{errors.image}</ErrorText>}
                    </UploadArea>
                </FormGrid>
            </ModalContent>

            <ModalContent>
                <Label>Visible To</Label>
                <HelperText>Choose the appropriate user group this playlist will be visible to in the app.</HelperText>
                <VisibleToRow>
                    <RadioButton
                        active={form.visibleToEmployees && !form.visibleToClients}
                        onClick={() => setAudience("employees")}
                    >
                        Employees
                    </RadioButton>
                    <RadioButton
                        active={!form.visibleToEmployees && form.visibleToClients}
                        onClick={() => setAudience("clients")}
                    >
                        Clients
                    </RadioButton>
                    <RadioButton
                        active={form.visibleToEmployees && form.visibleToClients}
                        onClick={() => setAudience("both")}
                    >
                        Employees & Clients
                    </RadioButton>
                </VisibleToRow>
                {errors.visibleTo && <ErrorText>{errors.visibleTo}</ErrorText>}
            </ModalContent>

            <ModalContent>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 18 }}>
                    <Field style={{ marginBottom: 0 }}>
                        <Label>Add Spotify URI</Label>
                        <TextInput
                            value={form.spotifyUri}
                            placeholder="Eg: spotify:user:spotify:playlist:37i9dQ"
                            onChange={({ target: { value } }) => setForm(prev => ({ ...prev, spotifyUri: value }))}
                        />
                    </Field>
                    <Field style={{ marginBottom: 0 }}>
                        <Label>Add Apple Music URL</Label>
                        <TextInput
                            value={form.appleMusicUrl}
                            placeholder="Eg: https://itunes.apple.com/us/playlist"
                            onChange={({ target: { value } }) => setForm(prev => ({ ...prev, appleMusicUrl: value }))}
                        />
                    </Field>
                </div>
                {errors.links && <ErrorText>{errors.links}</ErrorText>}
            </ModalContent>
        </StandardModal>
    );
}
